import { useState } from 'react';
import { ComboBox, ComboBoxFilterChangeEvent, ComboBoxProps } from '@progress/kendo-react-dropdowns';
import { Field, FieldRenderProps } from '@progress/kendo-react-form';
import { DialogProps, DisputeDialog, DisputeEventResult, DisputeField, FieldInput, FieldWrapper, FileUpload } from 'components';
import { DisputeEventTypes } from 'enums';
import { useDisputeService } from 'services';
import { useDisputeDetails, useInternalUsers } from 'state';
import { User } from 'types';
import { useFileUploadValidator } from 'hooks';


export type RouteDisputeProps = {
    toInternalUser: boolean
} & DialogProps;

/**
 * RouteDispute component allows the user to route a dispute to an internal user or to the supplier.
 * 
 * @param toInternalUser - true to route to an internal user; false to route to the supplier
 * @param props - Additional properties passed to the Dialog
 * @returns Rendered component
 */
export const RouteDispute = ({ toInternalUser, ...props }: RouteDisputeProps) => {
    const { submittedBy } = useDisputeDetails();
    const disputeService = useDisputeService();

    const filesValidator = useFileUploadValidator(0);

    const onSubmit = async (data: any) => {
        const { disputeNumber, files, user, ...rest } = data;

        const fileData = files?.map((file: any) => file.getRawFile()) ?? [];

        const result = await disputeService?.routeDisputeWithDocuments(disputeNumber, {
            ...rest,
            eventTypeId: toInternalUser ? DisputeEventTypes.DisputeRoutedToUser : DisputeEventTypes.DisputeRoutedToVendor,
            assigneeEmail: toInternalUser ? user.emailAddress  : submittedBy,
        },
        fileData);
        return {
            error: !result && 'Error routing dispute'
        } as DisputeEventResult;
    };

    return (
        <DisputeDialog
            initialValues={{ isPublic: false }}
            title={`Route to ${toInternalUser ? 'User' : 'Supplier'}`}
            routeToNext
            showNoteLabel
            submitLabel="Route"
            disableIsPublic={!toInternalUser}
            onSubmit={onSubmit}
            refreshDispute={true}
            {...props}
        >
                {toInternalUser &&
                    <DisputeField name="user" component={UserComboBox} validator={userValidator} />
                }


                {!toInternalUser &&  
                    <Field
                        component={FieldInput}
                        child={FileUpload}
                        label="Please provide any additional documentation."
                        name="files"
                        validator={filesValidator}
                    />
                }
        </DisputeDialog>
    );
};

const UserComboBox = (props: FieldRenderProps) => {
    const users = useInternalUsers().map(user => {
        const { firstName, lastName } = user;
        return {
            ...user,
            name: `${firstName} ${lastName}`,
            first: firstName.toLowerCase(),
            last: lastName.toLowerCase()
        };
    });
    const [data, setData] = useState<User[]>(users);

    const filterChange = ({ filter: { value } }: ComboBoxFilterChangeEvent) => {
        const search = value?.toLowerCase();
        setData(search?.length > 1 ? users.filter(({ first, last }) => first.startsWith(search) || last.startsWith(search)) : users);
    };

    return (
        <FieldWrapper label="User" required {...props}>
            <ComboBox
                id="user"
                aria-required="true"
                ariaLabelledBy="user_label"
                data={data}
                dataItemKey="emailAddress"
                textField="fullName"
                placeholder="Select a user"
                filterable={true}
                onFilterChange={filterChange}
                {...props as ComboBoxProps}
            />
        </FieldWrapper>
    );
};

const userValidator = (value: string) => value ? '' : 'Required. Please select a user';
